import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { useSesion } from './auth/contexto';
import { puedeEditarCRM, puedeGestionarCompras, puedeGestionarPersonas, puedeOperar, veInteligenciaCompras } from './lib/permisos';
import { PantallaCarga } from './components/Estados';
import { Tablero } from './pages/Tablero';

const Lista = lazy(() => import('./pages/Lista'));
const Gestiones = lazy(() => import('./pages/Gestiones'));
const MiTrabajo = lazy(() => import('./pages/MiTrabajo'));
const CasoPagina = lazy(() => import('./pages/caso/CasoPagina'));
const Aprobaciones = lazy(() => import('./pages/Aprobaciones'));
const Excepciones = lazy(() => import('./pages/Excepciones'));
const IndicadoresPagina = lazy(() => import('./pages/Indicadores'));
const Catalogos = lazy(() => import('./pages/Catalogos'));
const NoEncontrada = lazy(() => import('./pages/NoEncontrada'));
const Solicitudes = lazy(() => import('./pages/Solicitudes'));
const EquipoPagina = lazy(() => import('./pages/Equipo'));
const BrechasPagina = lazy(() => import('./pages/Brechas'));
const Biblioteca = lazy(() => import('./pages/Biblioteca'));
const Bandeja = lazy(() => import('./pages/Bandeja'));
const ContratacionPublica = lazy(() => import('./pages/ContratacionPublica'));
const Proveedores = lazy(() => import('./pages/Proveedores'));
const ProveedorDetalle = lazy(() => import('./pages/ProveedorDetalle'));
const InicioComercial = lazy(() => import('./pages/comercial/Inicio'));
const Oportunidades = lazy(() => import('./pages/comercial/Oportunidades'));
const Oportunidad = lazy(() => import('./pages/comercial/Oportunidad'));
const Cuentas = lazy(() => import('./pages/comercial/Cuentas'));
const Cuenta = lazy(() => import('./pages/comercial/Cuenta'));
const Prospectos = lazy(() => import('./pages/comercial/Prospectos'));
const ForecastComercial = lazy(() => import('./pages/comercial/Forecast'));
const CorreosPorVincular = lazy(() => import('./pages/comercial/CorreosPorVincular'));
const EquipoDigital = lazy(() => import('./pages/comercial/EquipoDigital'));
const TablerosComercial = lazy(() => import('./pages/comercial/Tableros'));
const CompetenciaComercial = lazy(() => import('./pages/comercial/Competencia'));
const AprobacionesComerciales = lazy(() => import('./pages/comercial/AprobacionesComerciales'));
const ComprasComercial = lazy(() => import('./pages/comercial/ComprasComercial'));
const InicioCompras = lazy(() => import('./pages/compras/Inicio'));
const TableroCompras = lazy(() => import('./pages/compras/Tablero'));
const SolicitudesCompras = lazy(() => import('./pages/compras/Solicitudes'));
const SolicitudCompras = lazy(() => import('./pages/compras/Solicitud'));
const ProveedoresCompras = lazy(() => import('./pages/compras/Proveedores'));
const ProveedorCompras = lazy(() => import('./pages/compras/Proveedores').then((m) => ({ default: m.ProveedorCompras })));
const CotizacionesCompras = lazy(() => import('./pages/compras/Cotizaciones'));
const InteligenciaCompras = lazy(() => import('./pages/compras/Inteligencia'));
const ProductoInteligencia = lazy(() => import('./pages/compras/Inteligencia').then((m) => ({ default: m.ProductoInteligencia })));
const InicioPersonas = lazy(() => import('./pages/personas/Inicio'));
const ColaboradoresPersonas = lazy(() => import('./pages/personas/Colaboradores'));
const ExpedientePersonas = lazy(() => import('./pages/personas/Expediente'));
const MiVidaLaboral = lazy(() => import('./pages/personas/Expediente').then((m) => ({ default: m.MiVidaLaboral })));
const OrganizacionPersonas = lazy(() => import('./pages/personas/Organizacion'));
const CargosPersonas = lazy(() => import('./pages/personas/Cargos'));
const CargoPersonas = lazy(() => import('./pages/personas/Cargo'));
const DirectorioPersonas = lazy(() => import('./pages/personas/Directorio'));
const IngresosPersonas = lazy(() => import('./pages/personas/Ingresos'));
const IngresoPersonas = lazy(() => import('./pages/personas/Ingreso'));
const TextosLegalesPersonas = lazy(() => import('./pages/personas/TextosLegales'));
const ContratosPersonas = lazy(() => import('./pages/personas/Contratos'));
const RequisitosPersonas = lazy(() => import('./pages/personas/Requisitos'));
const SeleccionPersonas = lazy(() => import('./pages/personas/Seleccion'));
const VacantePersonas = lazy(() => import('./pages/personas/Vacante'));
const CandidatoPersonas = lazy(() => import('./pages/personas/Candidato'));
const UsuariosPermisos = lazy(() => import('./pages/personas/Usuarios'));
const BandejaGestionHumana = lazy(() => import('./pages/personas/Bandeja'));

export function App() {
  return (
    <Suspense fallback={<PantallaCarga />}>
      <Routes>
        <Route path="/" element={<InicioSegunRol />} />
        <Route path="/preventa" element={<Layout />}>
          <Route index element={<Navigate to="/preventa/tablero" replace />} />
          <Route path="tablero" element={<Tablero />} />
          <Route path="lista" element={<Lista />} />
          <Route path="mi-trabajo" element={<MiTrabajo />} />
          <Route path="casos/:id" element={<CasoPagina />} />
          <Route path="aprobaciones" element={<Aprobaciones />} />
          <Route path="excepciones" element={<Excepciones />} />
          <Route path="indicadores" element={<IndicadoresPagina />} />
          <Route path="catalogos" element={<Catalogos />} />
          <Route path="solicitudes" element={<Solicitudes />} />
          <Route path="equipo" element={<EquipoPagina />} />
          <Route path="brechas" element={<BrechasPagina />} />
          <Route path="biblioteca" element={<Biblioteca />} />
          <Route path="bandeja" element={<Bandeja />} />
          <Route path="gestiones" element={<Gestiones />} />
          <Route path="contratacion" element={<ContratacionPublica />} />
          <Route path="proveedores" element={<Proveedores />} />
          <Route path="proveedores/:id" element={<ProveedorDetalle />} />
          <Route path="*" element={<NoEncontrada />} />
        </Route>
        <Route path="/comercial" element={<Layout />}>
          <Route index element={<InicioComercial />} />
          <Route path="oportunidades" element={<Oportunidades />} />
          <Route path="oportunidades/:id" element={<Oportunidad />} />
          <Route path="cuentas" element={<Cuentas />} />
          <Route path="cuentas/:id" element={<Cuenta />} />
          <Route path="prospectos" element={<Prospectos />} />
          <Route path="forecast" element={<ForecastComercial />} />
          <Route path="correos" element={<CorreosPorVincular />} />
          <Route path="equipo" element={<EquipoDigital />} />
          <Route path="tableros" element={<TablerosComercial />} />
          <Route path="competencia" element={<CompetenciaComercial />} />
          <Route path="aprobaciones" element={<AprobacionesComerciales />} />
          <Route path="compras" element={<ComprasComercial />} />
          <Route path="*" element={<NoEncontrada />} />
        </Route>
        <Route path="/compras" element={<Layout />}>
          <Route index element={<InicioComprasSegunRol />} />
          <Route path="bandeja" element={<InicioCompras />} />
          <Route path="solicitudes" element={<SolicitudesCompras />} />
          <Route path="solicitudes/:id" element={<SolicitudCompras />} />
          <Route path="proveedores" element={<ProveedoresCompras />} />
          <Route path="proveedores/:id" element={<ProveedorCompras />} />
          <Route path="cotizaciones" element={<CotizacionesCompras />} />
          <Route path="inteligencia" element={<InteligenciaCompras />} />
          <Route path="inteligencia/:id" element={<ProductoInteligencia />} />
          <Route path="*" element={<NoEncontrada />} />
        </Route>
        <Route path="/personas" element={<Layout />}>
          <Route index element={<InicioPersonasSegunRol />} />
          <Route path="colaboradores" element={<ColaboradoresPersonas />} />
          <Route path="personas/:personaId" element={<ExpedientePersonas />} />
          <Route path="organizacion" element={<OrganizacionPersonas />} />
          <Route path="cargos" element={<CargosPersonas />} />
          <Route path="cargos/:id" element={<CargoPersonas />} />
          <Route path="directorio" element={<DirectorioPersonas />} />
          <Route path="seleccion" element={<SeleccionPersonas />} />
          <Route path="seleccion/vacantes/:vacanteId" element={<VacantePersonas />} />
          <Route path="seleccion/candidatos/:personaId" element={<CandidatoPersonas />} />
          <Route path="ingresos" element={<IngresosPersonas />} />
          <Route path="ingresos/:id" element={<IngresoPersonas />} />
          <Route path="textos-legales" element={<TextosLegalesPersonas />} />
          <Route path="contratos" element={<ContratosPersonas />} />
          <Route path="requisitos" element={<RequisitosPersonas />} />
          <Route path="usuarios" element={<UsuariosPermisos />} />
          <Route path="bandeja" element={<BandejaGestionHumana />} />
          <Route path="mi" element={<MiVidaLaboral />} />
          <Route path="*" element={<NoEncontrada />} />
        </Route>
        <Route path="/preventa/comercial/*" element={<RedirigirComercial />} />
        <Route path="*" element={<Navigate to="/preventa/tablero" replace />} />
      </Routes>
    </Suspense>
  );
}

/** Enlaces viejos de la 2.3.0 (/preventa/comercial/...): Comercial es una aplicación aparte. */
function RedirigirComercial() {
  const resto = useParams()['*'] ?? '';
  return <Navigate to={`/comercial${resto ? `/${resto}` : ''}`} replace />;
}

/** En Compras, Vannessa, Gerencia y Finanzas empiezan en el tablero; quien solo pide (Preventa, Comercial) en sus solicitudes. */
function InicioComprasSegunRol() {
  const { roles } = useSesion();
  return veInteligenciaCompras(roles) ? <TableroCompras /> : <Navigate to="/compras/solicitudes" replace />;
}

/** En Personas, Gestión Humana y Gerencia empiezan en el inicio de Gestión Humana; los demás en Mi Vida Laboral. */
function InicioPersonasSegunRol() {
  const { roles } = useSesion();
  return puedeGestionarPersonas(roles) ? <InicioPersonas /> : <Navigate to="/personas/mi" replace />;
}

/** Quien solo tiene el rol Comercial entra a Comercial; quien solo tiene roles de Personas, a Personas; los demás, a Preventa. */
function InicioSegunRol() {
  const { roles } = useSesion();
  if (roles.length > 0 && roles.every((r) => r.startsWith('Personas.'))) return <Navigate to="/personas" replace />;
  if (puedeGestionarCompras(roles) && !puedeOperar(roles) && !puedeEditarCRM(roles)) return <Navigate to="/compras" replace />;
  return <Navigate to={puedeEditarCRM(roles) && !puedeOperar(roles) ? '/comercial' : '/preventa/tablero'} replace />;
}
